import * as React from "react";
import * as cn from "classnames/bind";
import { loginUser } from "page/user/service";
import Button from "content/button/button";
import * as styles from "./login-box.css";
import { COOKIE_USER_EXPIRE_DAYS, COOKIE_USER_JWT } from "ui/constants";
import Cookies from "js-cookie";

const cx = cn.bind(styles);

const inputBoxCn = (hasErrors: boolean) =>
    cx({
        input: true,
        inputWithError: hasErrors,
    });

const labelCn = () =>
    cx({
        label: true,
    });

const errorLabelCn = () =>
    cx({
        label: true,
        errorLabel: true,
    });

interface LoginProps {
    getResource: (key: string) => string;
    login: (user: any) => any;
    onSuccessfulLogin: () => any;
    swapToRegisterClicked: () => any;
    domain: string;
}

interface LoginState {
    userName: string;
    password: string;
    userNameErrorText: string | null;
    passwordErrorText: string | null;
    formErrorText: string | null;
}

class LoginForm extends React.Component<LoginProps, LoginState> {
    public state: LoginState = {
        userName: "",
        password: "",
        userNameErrorText: null,
        passwordErrorText: null,
        formErrorText: null,
    };

    private isFormValid = () => {
        const { userName, password } = this.state;
        const { getResource } = this.props;

        let userNameError = null;
        let passwordError = null;

        const mailRx = /\S+@\S+\.\S+/;

        if (!userName || userName === "") {
            userNameError = getResource("LogIn_Error_MissingEmail");
        } else if (!mailRx.test(userName)) {
            userNameError = getResource("LogIn_Error_WrongEmail");
        }

        if (!password || password === "") {
            passwordError = getResource("LogIn_Error_MissingPassword");
        }

        this.setState({
            userNameErrorText: userNameError,
            passwordErrorText: passwordError,
            formErrorText: null,
        });

        if (userNameError || passwordError) {
            return false;
        }

        return true;
    };

    private login = async () => {
        if (!this.isFormValid()) {
            return;
        }

        const { userName, password } = this.state;

        const { getResource } = this.props;

        const result = await loginUser(userName, password);

        const { success, data, message, status } = result;

        if (success) {
            const { id, roles, userName, email } = data.user;

            Cookies.set(COOKIE_USER_JWT, data.jwt, {
                expires: COOKIE_USER_EXPIRE_DAYS,
                domain: this.props.domain,
            });

            this.props.login({
                id,
                roles,
                authenticated: true,
                userName,
                email,
            });

            this.props.onSuccessfulLogin();

            this.setState({
                userName: "",
                password: "",
                formErrorText: null,
            });
        } else if (status === 401) {
            this.setState({
                formErrorText: getResource("LogIn_Error_MismatchPassword"),
            });
        } else {
            this.setState({
                formErrorText: getResource("LogIn_Error_GenericError"),
            });
        }
    };

    private swapToRegister = () => {
        this.props.swapToRegisterClicked();
    };

    private loginOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.keyCode === 13) {
            // enter key pressed
            e.preventDefault();
            e.stopPropagation();

            this.login();
        }
    };

    private loginClicked = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        this.login();
    };

    private changeUserName = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({
            userName: e.target.value,
        });

    private changePassword = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({
            password: e.target.value,
        });

    public render() {
        const {
            password,
            userName,
            userNameErrorText,
            passwordErrorText,
            formErrorText,
        } = this.state;

        const { getResource } = this.props;

        return (
            <React.Fragment>
                <form>
                    <div
                        className={"form-group"}
                        key={"user-page-login-header"}>
                        {getResource("LogIn_Text_Lead")}
                    </div>

                    <h3>{getResource("LogIn_Text_Header")}</h3>
                    <label className={labelCn()}>
                        {getResource("LogIn_Form_Email")}
                    </label>
                    <input
                        className={inputBoxCn(!!userNameErrorText)}
                        type="email"
                        required={true}
                        value={userName}
                        onKeyUp={this.loginOnEnter}
                        onChange={this.changeUserName}
                    />
                    <label className={errorLabelCn()}>
                        {userNameErrorText}
                    </label>
                    <label className={labelCn()}>
                        {getResource("LogIn_Form_Password")}
                    </label>
                    <input
                        className={inputBoxCn(!!passwordErrorText)}
                        type="password"
                        required={true}
                        value={password}
                        onKeyUp={this.loginOnEnter}
                        onChange={this.changePassword}
                    />
                    <label className={errorLabelCn()}>
                        {passwordErrorText}
                    </label>
                    <label
                        className={errorLabelCn()}
                        style={{ textAlign: "center" }}>
                        {formErrorText}
                    </label>
                    <Button
                        text={getResource("LogIn_Button_Login")}
                        width={"100%"}
                        onClick={this.loginClicked}
                    />
                    <div>
                        <br />
                        {getResource("LogIn_NoAccount")}{" "}
                        <strong>
                            <a
                                style={{ cursor: "pointer" }}
                                onClick={this.swapToRegister}>
                                {" "}
                                {getResource("LogIn_CreateAccountLink")}
                            </a>
                        </strong>
                    </div>
                    <div>
                        <br />
                        {getResource("LogIn_ForgottenPassword")}{" "}
                        <strong>
                            <a
                                style={{ cursor: "pointer" }}
                                href={getResource("Stm_ResetPasswordUrl")}>
                                {" "}
                                {getResource("LogIn_ResetPassword")}
                            </a>
                        </strong>
                    </div>
                </form>
            </React.Fragment>
        );
    }
}

export default LoginForm;
